import { EyeOutlined } from "@ant-design/icons";
import { Alert, Button, message, Modal, Space, Table, Tag, Typography } from "antd";
import { useEffect, useState } from "react";

import { landingPageKindLabels } from "@fitness-app/data-models/entities/LandingPage";
import {
  getLandingTemplateSlug,
  landingTemplates,
  type LandingTemplate,
} from "@fitness-app/data-models/landing-templates/index";

import { getTemplatePreviewUrl } from "./landingUrls";

interface Props {
  open: boolean;
  onClose: () => void;
  getTakenSlugs: () => Promise<Set<string>>;
  importTemplates: (templates: LandingTemplate[]) => Promise<void>;
}

/**
 * The offer's coded pages shipped as builder templates (TRE-2356). Adding one creates a
 * published builder page on its `szablony/…` slug: a funnel step can use it right away, and
 * it can be copied and edited like any other page.
 */
export const LandingTemplatesModal = ({ open, onClose, getTakenSlugs, importTemplates }: Props) => {
  const [takenSlugs, setTakenSlugs] = useState<Set<string> | null>(null);
  const [importing, setImporting] = useState<string | null>(null);

  const refresh = () => {
    void getTakenSlugs()
      .then(setTakenSlugs)
      .catch((error: Error) => message.error(error.message));
  };

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  // Nothing is offered until the taken slugs are known: importing over a page in use would
  // replace its edited config with the stock one.
  const missing = takenSlugs
    ? landingTemplates.filter((template) => !takenSlugs.has(getLandingTemplateSlug(template.key)))
    : [];

  const runImport = async (templates: LandingTemplate[], id: string) => {
    setImporting(id);
    try {
      await importTemplates(templates);
      message.success(templates.length === 1 ? "Szablon dodany" : `Dodano szablony: ${templates.length}`);
      refresh();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Nie udało się dodać szablonu");
    } finally {
      setImporting(null);
    }
  };

  return (
    <Modal
      title="Szablony z oferty"
      open={open}
      onCancel={onClose}
      width={960}
      footer={[
        <Button key="close" onClick={onClose}>
          Zamknij
        </Button>,
        <Button
          key="all"
          type="primary"
          disabled={!takenSlugs || !missing.length}
          loading={importing === "all"}
          onClick={() => runImport(missing, "all")}
        >
          Dodaj wszystkie brakujące ({missing.length})
        </Button>,
      ]}
    >
      <Space direction="vertical" className="w-full">
        <Alert
          type="info"
          showIcon
          message="Strony oferty z kodu jako szablony buildera"
          description="Dodany szablon jest od razu opublikowany i można go wybrać w kroku funnelu. Żeby zmienić treść bez wpływu na inne funnele, zrób kopię i edytuj kopię. Strony pod starymi adresami /oferta/… dalej działają z kodu."
        />
        <Table<LandingTemplate>
          dataSource={landingTemplates}
          rowKey="key"
          pagination={false}
          size="small"
          loading={!takenSlugs}
        >
          <Table.Column<LandingTemplate> title="Nazwa" dataIndex="name" />
          <Table.Column<LandingTemplate>
            title="Rodzaj"
            dataIndex="kind"
            render={(kind: LandingTemplate["kind"]) => <Tag color="purple">{landingPageKindLabels[kind]}</Tag>}
          />
          <Table.Column<LandingTemplate>
            title="Źródło"
            dataIndex="sourceRoute"
            responsive={["lg"]}
            render={(route: string) => <Typography.Text code>{route}</Typography.Text>}
          />
          <Table.Column<LandingTemplate>
            title=""
            render={(_, template) => {
              const added = takenSlugs?.has(getLandingTemplateSlug(template.key));
              return (
                <Space>
                  <Button
                    icon={<EyeOutlined />}
                    href={getTemplatePreviewUrl(template.key)}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                  {added ? (
                    <Tag color="green">Dodany</Tag>
                  ) : (
                    <Button
                      loading={importing === template.key}
                      disabled={!takenSlugs}
                      onClick={() => runImport([template], template.key)}
                    >
                      Dodaj
                    </Button>
                  )}
                </Space>
              );
            }}
          />
        </Table>
      </Space>
    </Modal>
  );
};
